import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import DeleteConfirmMenuItem from "components/Shared/ConfirmModal/DeleteConfirmMenuItem";
import { removeDeletedRecords } from "utils/apollo/cacheHelpers";
import { assertPresence } from "utils/assertions";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const fragment = gql(/* GraphQL */ `
  fragment DeleteProductLineMenuItemProductLine on ProductLine {
    id
    name
    isArchived
    skus {
      totalCount
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation DeleteProductLine($input: DeleteProductLineInput!) {
    deleteProductLine(input: $input) {
      productLine {
        id
        name
      }
    }
  }
`);

export type DeleteProductLineData = DocumentType<typeof mutation>;

type Props = {
  onCompleted?: (data: DeleteProductLineData) => void;
  productLine: FragmentType<typeof fragment> | null | undefined;
};

export default function DeleteProductLineMenuItem({
  onCompleted,
  productLine: productLineFragment,
}: Props) {
  const { showSuccessToast } = useAsyncCompleteToast();

  const productLine = getFragmentData(fragment, productLineFragment);

  const deleteMutation = useBrandMutation(mutation, {
    variables: productLine ? { input: { id: productLine.id } } : undefined,
    onCompleted: (data) => {
      onCompleted?.(data);
      const deletedProductLine = data?.deleteProductLine?.productLine;
      assertPresence(deletedProductLine);
      showSuccessToast(`Deleted ${deletedProductLine.name}`, {
        status: "warning",
      });
    },
    update: (cache, result) => {
      const deletedProductLine = result.data?.deleteProductLine?.productLine;
      assertPresence(deletedProductLine);
      removeDeletedRecords({
        cache,
        baseFieldName: "productLines",
        deletedRecords: [deletedProductLine],
      });
    },
  });

  return (
    <DeleteConfirmMenuItem
      confirmText={
        <>
          Are you sure you want to delete the <b>{productLine?.name}</b> product
          line and all of its SKUs? You won’t be able to undo this action
          afterwards.
        </>
      }
      menuItemProps={{
        isDisabled:
          !productLine?.id ||
          !(productLine.isArchived || productLine?.skus?.totalCount === 0),
      }}
      label="Permanently Delete Product Line"
      mutation={deleteMutation}
    />
  );
}
